import { useEffect, useRef, useState } from 'react'

import {
  Drawer,
  Header,
  type InitialUploadFile,
  PrimaryButton,
  SingleFileViewerUpload,
  Space,
  TertiaryButton,
  type UploadFile,
} from '@procurify/ui'

import { useFormatMedia } from '@webapp/core/hooks/useFormatMedia'
import { useProcurifyIntl } from '@webapp/hooks'
import { type ExpenseItem } from '@webapp/modules/expense/entities/ExpenseItem'
import {
  EditExpenseLineItemPresenter,
  type EditExpenseLineItemFormHandle,
} from '@webapp/modules/expense/features/edit-expense-line-item'
import messages from './messages'
import { EditExpenseLineItemDrawerContentStyled } from './styles/EditExpenseLineItemDrawerContentStyled'
import { EditExpenseLineItemDrawerDocumentStyled } from './styles/EditExpenseLineItemDrawerDocumentStyled'
import { EditExpenseLineItemDrawerFooterStyled } from './styles/EditExpenseLineItemDrawerFooterStyled'
import { useEditExpenseLineItemDrawer } from './useEditExpenseLineItemDrawer'

export interface IEditExpenseLineItemDrawerProps {
  drawerOpen: boolean
  onClose: () => void
  item: ExpenseItem
  expenseReportId: number
  requesterId: number
  secondaryCategoryId: number | undefined
  onSuccess: () => void
}

export const EditExpenseLineItemDrawer = ({
  drawerOpen,
  onClose,
  item,
  expenseReportId,
  requesterId,
  secondaryCategoryId,
  onSuccess,
}: IEditExpenseLineItemDrawerProps) => {
  const intl = useProcurifyIntl()
  const formatMedia = useFormatMedia()
  const formRef = useRef<EditExpenseLineItemFormHandle | null>(null)

  const [currentAttachment, setCurrentAttachment] = useState<string>(
    item.attachment ?? ''
  )

  useEffect(() => {
    setCurrentAttachment(item.attachment ?? '')
  }, [item])

  const {
    data: { reimbursementSetting, mileageConfiguration },
  } = useEditExpenseLineItemDrawer({ requesterId })

  const handleUploadComplete = (file: UploadFile) => {
    const uuid =
      file.rawResponse?.data?.uuid ??
      file.rawResponse?.data?.file ??
      file.file ??
      ''
    setCurrentAttachment(String(uuid))
  }

  const handleDeleteFile = () => {
    setCurrentAttachment('')
  }

  const initialFile: InitialUploadFile | undefined = currentAttachment
    ? {
        id: currentAttachment,
        file: formatMedia(currentAttachment),
      }
    : undefined

  const renderDocument = () => (
    <EditExpenseLineItemDrawerDocumentStyled>
      <SingleFileViewerUpload
        initialFile={initialFile}
        onUploadComplete={handleUploadComplete}
        onDelete={handleDeleteFile}
        disabled={false}
      />
    </EditExpenseLineItemDrawerDocumentStyled>
  )

  return (
    <Drawer
      drawerOpen={drawerOpen}
      setDrawerOpen={onClose}
      documentOpen={true}
      DocumentProps={{
        renderDocument,
        closeDrawerOnEsc: true,
      }}
      DrawerProps={{
        renderTitle: () => (
          <Header as='h4'>{intl.formatMessage(messages.drawerTitle)}</Header>
        ),
        renderFooter: () => (
          <EditExpenseLineItemDrawerFooterStyled>
            <Space direction='horizontal' justify='start' align='center'>
              <PrimaryButton
                type='button'
                onClick={() => formRef.current?.submitForm()}
              >
                {intl.formatMessage(messages.saveButton)}
              </PrimaryButton>
              <TertiaryButton type='button' onClick={onClose}>
                {intl.formatMessage(messages.cancelButton)}
              </TertiaryButton>
            </Space>
          </EditExpenseLineItemDrawerFooterStyled>
        ),
      }}
    >
      {() => (
        <EditExpenseLineItemDrawerContentStyled>
          <EditExpenseLineItemPresenter
            item={item}
            expenseReportId={expenseReportId}
            requesterId={requesterId}
            secondaryCategoryId={secondaryCategoryId}
            reimbursementSetting={reimbursementSetting}
            mileageConfiguration={mileageConfiguration}
            attachment={currentAttachment}
            onSuccess={onSuccess}
            formRef={formRef}
          />
        </EditExpenseLineItemDrawerContentStyled>
      )}
    </Drawer>
  )
}

EditExpenseLineItemDrawer.displayName = 'EditExpenseLineItemDrawer'
